// `search.page.tpl.html:959` — the criteria builder's eighth section, titled with the tenant's
// own ATS ("Bullhorn filters").
//
// ⚠️ IT IS A TABLE WHOSE COLUMNS CHANGE PER ROW. Every other repeating section has one header
// for the whole grid; this one renders `Field` plus whatever
// `atsFiltersTableHeadlinesByType[row.data.type]` says — one column ("Condition") for text,
// multi-select and single-select, two ("From"/"To") for numeric and date — and it renders that
// header INSIDE each row group, above that row. A single header row is the obvious shape and is
// wrong for any list mixing a date filter with a text one.
//
// The cells after Field are an `ng-include` chosen by the field's type, which is five separate
// templates upstream and five branches here.
import type { ReactElement } from 'react'
import { AtsFilterConditionType, AtsFiltersFieldType, type AtsFilterValue } from '@woo-ext/api'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import {
  ATS_FILTER_CONDITIONS,
  ATS_FILTER_HEADLINES,
  ATS_FILTER_SINGLESELECT_CONDITIONS,
  atsFilterTypeForSection,
  isAtsFilterDataValid,
  newAtsFilterData,
  type AtsFilterData,
  type AtsFilterRow,
} from './atsFilterData.ts'

export interface AtsFiltersFieldOption {
  section: string
  type: AtsFiltersFieldType
  name: string
  displayName: string
}

export interface AtsFiltersSectionProps {
  rows: readonly AtsFilterRow[]
  fields: readonly AtsFiltersFieldOption[]
  /** `atsFiltersSingleselectValueMap` — options per section, for SINGLE_SELECT rows. */
  singleselectValues?: Readonly<Record<string, readonly AtsFilterValue[]>>
  /** `form.atsFilters.$error.validAtsFilters` — the inline errors only show once it is set. */
  showErrors?: boolean
  onChange?: (index: number, row: AtsFilterRow) => void
  onRemove?: (index: number) => void
  onAdd?: () => void
}

function ConditionSelect({
  value,
  options,
  onChange,
}: {
  value: AtsFilterConditionType | undefined
  options: readonly { display: string; value: AtsFilterConditionType }[]
  onChange: (condition: AtsFilterConditionType) => void
}): ReactElement {
  return (
    <div className="ats-filters-condition criteria-cell">
      <div className="select">
        <select
          className="form-control input"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value as AtsFilterConditionType)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.display}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

const NEEDS_INPUT = 'Heads up! This field needs your input'

function ValueCells({
  index,
  row,
  singleselectValues,
  invalid,
  onData,
}: {
  index: number
  row: AtsFilterRow
  singleselectValues: Readonly<Record<string, readonly AtsFilterValue[]>>
  invalid: boolean
  onData: (data: AtsFilterData) => void
}): ReactElement | null {
  const data = row.data
  if (data === undefined) return null

  switch (data.type) {
    case AtsFiltersFieldType.Text:
      return (
        <>
          <ConditionSelect
            value={data.condition}
            options={ATS_FILTER_CONDITIONS}
            onChange={(condition) => onData({ ...data, condition })}
          />
          <div className="ats-filters-text-input criteria-cell">
            <div>
              {/* Free text: this one has no `add-from-autocomplete-only` and no autocomplete —
                  the values are whatever the ATS field contains. */}
              <TagsInput
                className={`tags-input-default-width${invalid ? ' ats-filters-tags-field-error' : ''}`}
                name={`ats-filters-text-${String(index)}`}
                placeholder="Add values"
                tags={data.values.map((text) => ({ text }))}
                freeText
                onAdd={(tag) => onData({ ...data, values: [...data.values, tag.text] })}
                onRemove={(at) => onData({ ...data, values: data.values.filter((_, i) => i !== at) })}
              />
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )

    case AtsFiltersFieldType.MultiSelect:
      return (
        <>
          <ConditionSelect
            value={data.condition}
            options={ATS_FILTER_CONDITIONS}
            onChange={(condition) => onData({ ...data, condition })}
          />
          <div className="ats-filters-text-input criteria-cell">
            <div>
              {/* ⚠️ THE SAME CELL CLASS AS TEXT, AND A DIFFERENT FIELD. This one IS
                  autocomplete-only, against `getAtsFiltersMultiselectAutocompleteValues` for
                  the row's section, and its tags are `{id, text}` rather than strings. The
                  autocomplete source lands with the typeahead wiring; the field is otherwise
                  complete. */}
              <TagsInput
                className={`tags-input-default-width${invalid ? ' ats-filters-tags-field-error' : ''}`}
                name={`ats-filters-text-${String(index)}`}
                placeholder="Add values"
                tags={data.values.map((value): Tag => ({ text: value.text }))}
                onAdd={(tag) => onData({ ...data, values: [...data.values, { id: tag.text, text: tag.text }] })}
                onRemove={(at) => onData({ ...data, values: data.values.filter((_, i) => i !== at) })}
              />
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )

    case AtsFiltersFieldType.SingleSelect: {
      const options = row.section === undefined ? [] : (singleselectValues[row.section] ?? [])
      return (
        <>
          <ConditionSelect
            value={data.condition}
            options={ATS_FILTER_SINGLESELECT_CONDITIONS}
            onChange={(condition) => onData({ ...data, condition })}
          />
          <div className="ats-filters-text-input criteria-cell">
            <div className="select">
              <select
                className={`form-control input${invalid ? ' ats-filters-select-field-error' : ''}`}
                value={data.value?.id ?? ''}
                onChange={(event) =>
                  onData({ ...data, value: options.find((option) => option.id === event.target.value) ?? null })
                }
              >
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
            {invalid ? <label className="control-label has-error">{NEEDS_INPUT}</label> : null}
          </div>
        </>
      )
    }

    case AtsFiltersFieldType.Numeric:
      return (
        <>
          <div className="ats-filters-number-value criteria-cell">
            <input
              name={`atsFiltersMinYears${String(index)}`}
              className={`form-control input${data.min === null && invalid ? ' ats-filters-input-field-error' : ''}`}
              type="number"
              min={0}
              required
              value={data.min ?? ''}
              onChange={(event) =>
                onData({ ...data, min: event.target.value === '' ? null : Number(event.target.value) })
              }
            />
          </div>
          <div className="ats-filters-number-value criteria-cell">
            <input
              name={`atsFiltersMaxYears${String(index)}`}
              className={`form-control input${data.max === null && invalid ? ' ats-filters-input-field-error' : ''}`}
              type="number"
              // ⚠️ `ng-min="filter.data.min || 0"` — the floor is the OTHER input's value, not a
              // constant, and it falls back to 0 when that is empty or zero.
              min={data.min ?? 0}
              required
              value={data.max ?? ''}
              onChange={(event) =>
                onData({ ...data, max: event.target.value === '' ? null : Number(event.target.value) })
              }
            />
            {invalid ? (
              <label className="control-label has-error">
                {data.min !== null && data.max !== null && data.max < data.min
                  ? "'To' must be greater than 'From'"
                  : 'Heads up! Some fields need your input'}
              </label>
            ) : null}
          </div>
        </>
      )

    case AtsFiltersFieldType.Date:
      return (
        <>
          <div className="ats-filters-date-value criteria-cell">
            {/* `uib-datepicker-popup="dd-MM-yyyy"` — the popup calendar is its own widget and
                arrives with it; the input and its placeholder are what the screen shows. */}
            <input
              className="form-control input"
              placeholder="DD-MM-YYYY"
              value={formatDate(data.from)}
              onChange={(event) => onData({ ...data, from: parseDate(event.target.value) })}
            />
          </div>
          <div className="ats-filters-date-value criteria-cell">
            <input
              className={`form-control input${invalid ? ' ats-filters-input-field-error' : ''}`}
              placeholder="DD-MM-YYYY"
              required
              value={formatDate(data.to)}
              onChange={(event) => onData({ ...data, to: parseDate(event.target.value) })}
            />
            {invalid ? (
              <label className="control-label has-error">
                {/* ⚠️ THE TERNARY IS THE OTHER WAY ROUND FROM THE NUMERIC ONE. Here the
                    ORDERED case shows "needs your input" and the disordered case names the
                    ordering — upstream writes `to > from ? needsInput : fromEarlier`. */}
                {data.to !== null && data.from !== null && data.to > data.from
                  ? NEEDS_INPUT
                  : 'From date must be earlier than To date'}
              </label>
            ) : null}
          </div>
        </>
      )
  }
}

/** `dd-MM-yyyy`, the datepicker's format. */
function formatDate(date: Date | null): string {
  if (date === null) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${String(date.getFullYear())}`
}

function parseDate(text: string): Date | null {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text)
  if (match === null) return null
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
}

export function AtsFiltersSection({
  rows,
  fields,
  singleselectValues = {},
  showErrors = false,
  onChange,
  onRemove,
  onAdd,
}: AtsFiltersSectionProps): ReactElement {
  // ⚠️ ORDERED BY `name`, DISPLAYED BY `displayName`. `orderBy:'name'` upstream, and the two
  // differ — `CUSTOM_KEYWORDS1` sorts where its enum name falls, not where its label does.
  const sortedFields = [...fields].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="ats-filters-criteria criteria-table">
      {rows.map((row, index) => {
        const headlines = row.data === undefined ? [] : ATS_FILTER_HEADLINES[row.data.type]
        const invalid =
          showErrors && row.section !== undefined && row.section !== '' && !isAtsFilterDataValid(row.data)
        return (
          // eslint-disable-next-line react/no-array-index-key -- positional upstream too.
          <div className="ats-filters-row-group sub-section" key={index}>
            <div className="criteria-row">
              <div className="criteria-cell-label">
                <label className="bold-soft">Field</label>
              </div>
              {headlines.map((label) => (
                <div className="criteria-cell-label" key={label}>
                  <label className="bold-soft">{label}</label>
                </div>
              ))}
            </div>
            <div className="criteria-row">
              <div className="ats-filters-field criteria-cell">
                <div className="select">
                  <select
                    className="form-control input"
                    value={row.section ?? ''}
                    onChange={(event) => {
                      const section = event.target.value
                      const type = atsFilterTypeForSection(fields, section)
                      // The data is REPLACED, not merged — see `newAtsFilterData`.
                      onChange?.(index, {
                        section,
                        ...(type === undefined ? {} : { data: newAtsFilterData(type) }),
                      })
                    }}
                  >
                    <option className="color-graylight" value="" disabled>
                      Please select...
                    </option>
                    {sortedFields.map((field) => (
                      <option key={field.section} value={field.section}>
                        {field.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                {showErrors && (row.section === undefined || row.section === '') ? (
                  <label className="control-label has-error">Please pick a field</label>
                ) : null}
              </div>
              <ValueCells
                index={index}
                row={row}
                singleselectValues={singleselectValues}
                invalid={invalid}
                onData={(data) => onChange?.(index, { ...row, data })}
              />
              <div
                className="remove-criteria-container criteria-cell"
                role="button"
                aria-label="Remove"
                tabIndex={0}
                onClick={() => onRemove?.(index)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onRemove?.(index)
                }}
              >
                <div className="fa-stack fa-1x icons-container">
                  <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
                  <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
                </div>
              </div>
            </div>
          </div>
        )
      })}

      <div className="criteria-row bottom-cta-row sub-section">
        {/* Two placeholders, each with its OWN class here — the keyword section's are bare
            `placeholder-cell`s. */}
        <div className="criteria-cell placeholder-cell ats-filters-first-placeholder" />
        <div className="criteria-cell placeholder-cell ats-filters-second-placeholder" />
        <div className="ats-filters-add-filter criteria-cell">
          <a
            href=""
            className="add-button"
            onClick={(event) => {
              event.preventDefault()
              onAdd?.()
            }}
          >
            <i className="fal fa-plus-circle" />
            <span>&nbsp;Add</span>
          </a>
        </div>
      </div>
    </div>
  )
}
